// src/pages/Intro.js - 완전한 트렌디한 스포츠 랭킹 앱 소개
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// 애니메이션 키프레임
const animations = `
  @keyframes float {
    0% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
    100% { transform: translateY(0px); }
  }
  
  @keyframes glow {
    0% { box-shadow: 0 0 20px rgba(102, 126, 234, 0.3); }
    50% { box-shadow: 0 0 30px rgba(102, 126, 234, 0.6), 0 0 50px rgba(102, 126, 234, 0.3); }
    100% { box-shadow: 0 0 20px rgba(102, 126, 234, 0.3); }
  }
  
  @keyframes slideUp {
    from { transform: translateY(30px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
  
  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  }
`;

// 상단바 주요 메뉴 설명
const navbarInfo = [
  { icon: "🎯", label: "매칭", desc: "실력 기반 스마트 매칭", color: "#667eea" },
  { icon: "🏆", label: "랭킹", desc: "실시간 개인/길드 랭킹", color: "#f093fb" },
  { icon: "🚩", label: "길드", desc: "팀 생성 및 멤버 관리", color: "#4facfe" },
  { icon: "💬", label: "커뮤니티", desc: "소통과 정보 공유", color: "#a8edea" },
  { icon: "🔔", label: "알림", desc: "실시간 알림 시스템", color: "#ffd89b" },
  { icon: "👤", label: "프로필", desc: "내 정보 및 전적 관리", color: "#c471ed" },
];

// 핵심 기능 소개
const coreFeatures = [
  {
    icon: "🎳",
    title: "스마트 매칭",
    subtitle: "MMR 기반 실력 매칭",
    description: "볼링 전용 MMR 시스템으로 비슷한 실력의 상대와 공정한 경기를 즐기세요",
    features: ["3게임 토탈 스코어 방식", "실력 기반 자동 매칭", "지역별 매칭 시스템", "랭킹전/친선전 구분"]
  },
  {
    icon: "🏰",
    title: "길드 시스템",
    subtitle: "팀플레이의 새로운 차원",
    description: "동료들과 길드를 결성하여 팀 랭킹전에 도전하고 길드 이벤트를 즐기세요",
    features: ["길드 생성 및 관리", "멤버 등급 시스템", "길드 랭킹전/친선전", "출석체크 & 포인트"]
  },
  {
    icon: "🏛️",
    title: "협회 & 대회",
    subtitle: "공식 대회 시스템",
    description: "협회에서 주최하는 공식 대회에 참가하여 실력을 인정받으세요",
    features: ["대회 주최 및 관리", "자동 대진표 생성", "참가 신청 시스템", "공식 기록 관리"]
  }
];

// MMR 티어 시스템
const tierSystem = [
  { name: "브론즈", emoji: "🥉", range: "0 ~ 999", color: "#cd7f32", desc: "볼링 입문자, 기본기 학습 단계" },
  { name: "실버", emoji: "🥈", range: "1000 ~ 1399", color: "#c0c0c0", desc: "안정적인 폼으로 꾸준한 성장" },
  { name: "골드", emoji: "🥇", range: "1400 ~ 1799", color: "#ffd700", desc: "중급자 실력, 스페어 처리 능숙" },
  { name: "플래티넘", emoji: "💎", range: "1800 ~ 2199", color: "#e5e4e2", desc: "상위 10% 고수, 200점 게임 가능" },
  { name: "다이아몬드", emoji: "💍", range: "2200+", color: "#b9f2ff", desc: "최상위 볼러, 퍼펙트 게임 경험자" }
];

// 사용자 유형별 혜택
const userTypeInfo = {
  개인: {
    title: "🎯 개인 사용자",
    subtitle: "볼링을 즐기는 모든 분들을 위한 완벽한 플랫폼",
    benefits: [
      "실력 기반 스마트 매칭으로 공정한 경기",
      "개인 MMR 관리 및 상세한 전적 분석",
      "지역별 볼링장 정보 및 매칭",
      "친구 추가 및 친선전 시스템",
      "길드 가입으로 팀플레이 경험",
      "커뮤니티 참여 및 정보 공유"
    ],
    color: "#667eea"
  },
  협회: {
    title: "🏛️ 협회 사용자",
    subtitle: "볼링 대회를 주최하고 운영하는 전문 시스템",
    benefits: [
      "대회 생성 및 전문적인 운영 도구",
      "참가자 모집 및 자동 승인/거절",
      "MMR 기반 자동 대진표 생성",
      "경기 결과 관리 및 공식 기록",
      "상금/상품 관리 시스템",
      "참가자 통계 및 대회 분석"
    ],
    color: "#f5576c"
  }
};

function Intro() {
  const navigate = useNavigate();
  const [selectedFeature, setSelectedFeature] = useState(0);
  const [selectedUserType, setSelectedUserType] = useState("개인");
  const [animatedElements, setAnimatedElements] = useState(new Set());

  // 자동 슬라이드 기능
  useEffect(() => {
    const interval = setInterval(() => {
      setSelectedFeature(prev => (prev + 1) % coreFeatures.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // 스크롤 애니메이션
  useEffect(() => {
    const handleScroll = () => {
      const elements = document.querySelectorAll('[data-animate]');
      elements.forEach((el, index) => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.8) {
          setAnimatedElements(prev => new Set([...prev, index]));
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // 초기 실행
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <style>{animations}</style>
      <div style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)",
        position: "relative",
        overflow: "hidden"
      }}>
        {/* 배경 파티클 효과 */}
        <div style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(120, 219, 255, 0.2) 0%, transparent 50%)
          `,
          zIndex: 1
        }} />

        {/* 메인 컨테이너 */}
        <div style={{
          maxWidth: 1200,
          margin: "0 auto",
          padding: "60px 20px",
          position: "relative",
          zIndex: 2
        }}>
          {/* 히어로 섹션 */}
          <div style={{
            textAlign: "center",
            marginBottom: 80,
            animation: "slideUp 1s ease-out"
          }}>
            <div style={{
              fontSize: 64,
              marginBottom: 20,
              animation: "float 3s ease-in-out infinite"
            }}>
              🎳
            </div>
            
            <h1 style={{
              fontSize: 48,
              fontWeight: 900,
              color: "white",
              marginBottom: 20,
              textShadow: "0 4px 20px rgba(0,0,0,0.3)",
              letterSpacing: "-2px",
              lineHeight: 1.2
            }}>
              Sportier
            </h1>
            
            <p style={{
              fontSize: 24,
              color: "rgba(255,255,255,0.9)",
              marginBottom: 40,
              fontWeight: 600,
              textShadow: "0 2px 10px rgba(0,0,0,0.2)"
            }}>
              스마트 MMR 시스템으로 만나는<br />
              <span style={{
                background: "linear-gradient(45deg, #FFD700, #FFA500)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontWeight: 800
              }}>
                차세대 스포츠 랭킹 플랫폼
              </span>
            </p>

            <div style={{
              display: "flex",
              gap: 20,
              justifyContent: "center",
              flexWrap: "wrap"
            }}>
              <button
                onClick={() => {
                  const user = localStorage.getItem("user");
                  if (user) {
                    navigate("/mypage");
                  } else {
                    navigate("/LoginRegister");
                  }
                }}
                style={{
                  background: "linear-gradient(45deg, #FFD700, #FFA500)",
                  color: "#1a1a1a",
                  border: "none",
                  borderRadius: 50,
                  padding: "20px 40px",
                  fontSize: 20,
                  fontWeight: 900,
                  cursor: "pointer",
                  boxShadow: "0 10px 30px rgba(255, 215, 0, 0.5)",
                  transition: "all 0.3s ease",
                  animation: "pulse 2s ease-in-out infinite"
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "translateY(-5px) scale(1.05)";
                  e.target.style.boxShadow = "0 15px 40px rgba(255, 215, 0, 0.7)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateY(0) scale(1)";
                  e.target.style.boxShadow = "0 10px 30px rgba(255, 215, 0, 0.5)";
                }}
              >
                🚀 무료 회원가입
              </button>
              
              <button
                onClick={() => {
                  const user = localStorage.getItem("user");
                  if (user) {
                    navigate("/mypage");
                  } else {
                    navigate("/login");
                  }
                }}
                style={{
                  background: "rgba(255,255,255,0.2)",
                  color: "white",
                  border: "2px solid rgba(255,255,255,0.3)",
                  borderRadius: 50,
                  padding: "18px 38px",
                  fontSize: 20,
                  fontWeight: 800,
                  cursor: "pointer",
                  backdropFilter: "blur(10px)",
                  transition: "all 0.3s ease"
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "rgba(255,255,255,0.3)";
                  e.target.style.transform = "translateY(-3px)";
                  e.target.style.borderColor = "rgba(255,255,255,0.5)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "rgba(255,255,255,0.2)";
                  e.target.style.transform = "translateY(0)";
                  e.target.style.borderColor = "rgba(255,255,255,0.3)";
                }}
              >
                ⚡ 기존 회원 로그인
              </button>
            </div>

            {/* 추가 정보 */}
            <div style={{
              marginTop: 50,
              display: "flex",
              justifyContent: "center",
              gap: 40,
              flexWrap: "wrap"
            }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                color: "rgba(255,255,255,0.8)",
                fontSize: 14,
                fontWeight: 600
              }}>
                <span style={{fontSize: 20}}>✅</span>
                완전 무료 서비스
              </div>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                color: "rgba(255,255,255,0.8)",
                fontSize: 14,
                fontWeight: 600
              }}>
                <span style={{fontSize: 20}}>🎯</span>
                실력 기반 매칭
              </div>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                color: "rgba(255,255,255,0.8)",
                fontSize: 14,
                fontWeight: 600
              }}>
                <span style={{fontSize: 20}}>📱</span>
                모바일 최적화
              </div>
            </div>
          </div>

          {/* 핵심 기능 슬라이더 */}
          <div 
            data-animate
            style={{
              background: "rgba(255,255,255,0.1)",
              backdropFilter: "blur(20px)",
              borderRadius: 30,
              padding: 40,
              marginBottom: 60,
              border: "1px solid rgba(255,255,255,0.2)",
              animation: animatedElements.has(0) ? "slideUp 0.8s ease-out" : "none"
            }}
          >
            <h2 style={{
              fontSize: 32,
              fontWeight: 800,
              color: "white",
              textAlign: "center",
              marginBottom: 40,
              textShadow: "0 2px 10px rgba(0,0,0,0.3)"
            }}>
              ✨ 핵심 기능
            </h2>

            {/* 기능 선택 탭 */}
            <div style={{
              display: "flex",
              justifyContent: "center",
              gap: 10,
              marginBottom: 40,
              flexWrap: "wrap"
            }}>
              {coreFeatures.map((feature, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedFeature(index)}
                  style={{
                    background: selectedFeature === index 
                      ? "rgba(255,255,255,0.9)" 
                      : "rgba(255,255,255,0.2)",
                    color: selectedFeature === index ? "#1a1a1a" : "white",
                    border: "none",
                    borderRadius: 20,
                    padding: "12px 24px",
                    fontSize: 16,
                    fontWeight: 700,
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    backdropFilter: "blur(10px)"
                  }}
                >
                  {feature.icon} {feature.title}
                </button>
              ))}
            </div>

            {/* 선택된 기능 상세 */}
            <div style={{
              background: "rgba(255,255,255,0.1)",
              borderRadius: 20,
              padding: 30,
              border: "1px solid rgba(255,255,255,0.2)"
            }}>
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 2fr",
                gap: 40,
                alignItems: "center"
              }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{
                    fontSize: 80,
                    marginBottom: 20,
                    animation: "pulse 2s ease-in-out infinite"
                  }}>
                    {coreFeatures[selectedFeature].icon}
                  </div>
                  <h3 style={{
                    fontSize: 24,
                    fontWeight: 800,
                    color: "white",
                    marginBottom: 10
                  }}>
                    {coreFeatures[selectedFeature].title}
                  </h3>
                  <p style={{
                    fontSize: 16,
                    color: "rgba(255,255,255,0.8)",
                    fontWeight: 600
                  }}>
                    {coreFeatures[selectedFeature].subtitle}
                  </p>
                </div>
                
                <div>
                  <p style={{
                    fontSize: 18,
                    color: "white",
                    marginBottom: 25,
                    lineHeight: 1.6
                  }}>
                    {coreFeatures[selectedFeature].description}
                  </p>
                  
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: 15
                  }}>
                    {coreFeatures[selectedFeature].features.map((feature, idx) => (
                      <div
                        key={idx}
                        style={{
                          background: "rgba(255,255,255,0.1)",
                          padding: "12px 16px",
                          borderRadius: 12,
                          fontSize: 14,
                          color: "white",
                          fontWeight: 600,
                          textAlign: "center",
                          border: "1px solid rgba(255,255,255,0.2)"
                        }}
                      >
                        ✓ {feature}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 사용자 유형별 안내 */}
          <div 
            data-animate
            style={{
              background: "rgba(255,255,255,0.1)",
              backdropFilter: "blur(20px)",
              borderRadius: 30,
              padding: 40,
              marginBottom: 60,
              border: "1px solid rgba(255,255,255,0.2)",
              animation: animatedElements.has(1) ? "slideUp 0.8s ease-out 0.2s both" : "none"
            }}
          >
            <h2 style={{
              fontSize: 32,
              fontWeight: 800,
              color: "white",
              textAlign: "center",
              marginBottom: 40,
              textShadow: "0 2px 10px rgba(0,0,0,0.3)"
            }}>
              👥 사용자 유형별 혜택
            </h2>

            {/* 사용자 유형 선택 */}
            <div style={{
              display: "flex",
              justifyContent: "center",
              gap: 20,
              marginBottom: 40
            }}>
              {Object.keys(userTypeInfo).map(type => (
                <button
                  key={type}
                  onClick={() => setSelectedUserType(type)}
                  style={{
                    background: selectedUserType === type 
                      ? userTypeInfo[type].color 
                      : "rgba(255,255,255,0.2)",
                    color: "white",
                    border: "none",
                    borderRadius: 20,
                    padding: "16px 32px",
                    fontSize: 18,
                    fontWeight: 700,
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    boxShadow: selectedUserType === type 
                      ? `0 8px 25px ${userTypeInfo[type].color}40` 
                      : "none"
                  }}
                >
                  {userTypeInfo[type].title}
                </button>
              ))}
            </div>

            {/* 선택된 유형 상세 정보 */}
            <div style={{
              background: "rgba(255,255,255,0.1)",
              borderRadius: 20,
              padding: 30,
              border: "1px solid rgba(255,255,255,0.2)"
            }}>
              <h3 style={{
                fontSize: 24,
                fontWeight: 800,
                color: "white",
                marginBottom: 10,
                textAlign: "center"
              }}>
                {userTypeInfo[selectedUserType].title}
              </h3>
              
              <p style={{
                fontSize: 16,
                color: "rgba(255,255,255,0.8)",
                textAlign: "center",
                marginBottom: 30
              }}>
                {userTypeInfo[selectedUserType].subtitle}
              </p>

              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                gap: 20
              }}>
                {userTypeInfo[selectedUserType].benefits.map((benefit, idx) => (
                  <div
                    key={idx}
                    style={{
                      background: "rgba(255,255,255,0.1)",
                      padding: "16px 20px",
                      borderRadius: 15,
                      fontSize: 15,
                      color: "white",
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      border: "1px solid rgba(255,255,255,0.2)"
                    }}
                  >
                    <span style={{
                      background: userTypeInfo[selectedUserType].color,
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      flexShrink: 0
                    }} />
                    {benefit}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* MMR 티어 시스템 */}
          <div 
            data-animate
            style={{
              background: "rgba(255,255,255,0.1)",
              backdropFilter: "blur(20px)",
              borderRadius: 30,
              padding: 40,
              marginBottom: 60,
              border: "1px solid rgba(255,255,255,0.2)",
              animation: animatedElements.has(2) ? "slideUp 0.8s ease-out 0.4s both" : "none"
            }}
          >
            <h2 style={{
              fontSize: 32,
              fontWeight: 800,
              color: "white",
              textAlign: "center",
              marginBottom: 40,
              textShadow: "0 2px 10px rgba(0,0,0,0.3)"
            }}>
              🏆 MMR 티어 시스템
            </h2>

            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: 20,
              marginBottom: 30
            }}>
              {tierSystem.map((tier, idx) => (
                <div
                  key={tier.name}
                  style={{
                    background: `linear-gradient(135deg, ${tier.color}20, ${tier.color}10)`,
                    border: `2px solid ${tier.color}40`,
                    borderRadius: 20,
                    padding: 25,
                    textAlign: "center",
                    transition: "all 0.3s ease",
                    cursor: "pointer"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-5px)";
                    e.currentTarget.style.boxShadow = `0 10px 30px ${tier.color}30`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <div style={{
                    fontSize: 40,
                    marginBottom: 15,
                    animation: "float 3s ease-in-out infinite",
                    animationDelay: `${idx * 0.2}s`
                  }}>
                    {tier.emoji}
                  </div>
                  
                  <h4 style={{
                    fontSize: 20,
                    fontWeight: 800,
                    color: "white",
                    marginBottom: 8
                  }}>
                    {tier.name}
                  </h4>
                  
                  <p style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: tier.color,
                    marginBottom: 12
                  }}>
                    {tier.range}
                  </p>
                  
                  <p style={{
                    fontSize: 13,
                    color: "rgba(255,255,255,0.8)",
                    lineHeight: 1.4
                  }}>
                    {tier.desc}
                  </p>
                </div>
              ))}
            </div>

            <div style={{
              background: "rgba(255,255,255,0.1)",
              borderRadius: 15,
              padding: 20,
              textAlign: "center",
              border: "1px solid rgba(255,255,255,0.2)"
            }}>
              <p style={{
                fontSize: 16,
                color: "white",
                fontWeight: 600,
                lineHeight: 1.6,
                margin: 0
              }}>
                💡 <strong>볼링 전용 MMR 시스템</strong>으로 3게임 토탈 스코어를 기반으로 정확한 실력을 측정합니다.<br />
                상대의 실력과 경기 결과에 따라 <strong style={{color: "#FFD700"}}>공정하고 정교한 점수 변동</strong>이 이루어집니다!
              </p>
            </div>
          </div>

          {/* 상단바 메뉴 안내 */}
          <div 
            data-animate
            style={{
              background: "rgba(255,255,255,0.1)",
              backdropFilter: "blur(20px)",
              borderRadius: 30,
              padding: 40,
              border: "1px solid rgba(255,255,255,0.2)",
              animation: animatedElements.has(3) ? "slideUp 0.8s ease-out 0.6s both" : "none"
            }}
          >
            <h2 style={{
              fontSize: 32,
              fontWeight: 800,
              color: "white",
              textAlign: "center",
              marginBottom: 40,
              textShadow: "0 2px 10px rgba(0,0,0,0.3)"
            }}>
              🧭 주요 메뉴 가이드
            </h2>

            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: 20
            }}>
              {navbarInfo.map((item, idx) => (
                <div
                  key={item.label}
                  style={{
                    background: "rgba(255,255,255,0.1)",
                    borderRadius: 20,
                    padding: 25,
                    textAlign: "center",
                    border: "1px solid rgba(255,255,255,0.2)",
                    transition: "all 0.3s ease",
                    cursor: "pointer"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-3px)";
                    e.currentTarget.style.background = `${item.color}20`;
                    e.currentTarget.style.borderColor = `${item.color}40`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.background = "rgba(255,255,255,0.1)";
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)";
                  }}
                >
                  <div style={{
                    fontSize: 36,
                    marginBottom: 15,
                    animation: "float 3s ease-in-out infinite",
                    animationDelay: `${idx * 0.1}s`
                  }}>
                    {item.icon}
                  </div>
                  
                  <h4 style={{
                    fontSize: 18,
                    fontWeight: 800,
                    color: "white",
                    marginBottom: 8
                  }}>
                    {item.label}
                  </h4>
                  
                  <p style={{
                    fontSize: 14,
                    color: "rgba(255,255,255,0.8)",
                    lineHeight: 1.4,
                    margin: 0
                  }}>
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 하단 파도 효과 */}
        <div style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: 100,
          background: "linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.1) 100%)",
          zIndex: 1
        }} />
      </div>
      <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 9999 }}>
        <button onClick={() => window.location.href = '/'} style={{ padding: '16px 40px', borderRadius: 24, background: '#10b981', color: '#fff', fontWeight: 800, fontSize: 20, border: 'none', cursor: 'pointer', boxShadow: '0 4px 16px #10b98133', letterSpacing: 1 }}>
          홈 화면으로 이동
        </button>
      </div>
    </>
  );
}

export default Intro;